import { z } from 'zod';
import { paginationQuery, uuid } from './common.js';
const optText = (max) => z.string().trim().max(max).nullable().optional();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const hhmm = z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/, 'HH:MM');
export const REGISTRATION_STATUSES = [
    'none',
    'registered',
    'declined',
    'waitlist',
    'excused',
    'attended',
    'absent',
];
// ── training groups ─────────────────────────────────────────────────────────
export const trainingGroupWritable = z.object({
    name: z.string().trim().min(1).max(120),
    description: optText(2000),
    defaultTeamId: uuid.nullable().optional(),
    color: optText(20),
    isActive: z.boolean().optional(),
});
// ── training series ─────────────────────────────────────────────────────────
export const trainingSeriesBase = z.object({
    title: z.string().trim().min(1).max(160),
    description: optText(2000),
    /** May target more than one training group at once (e.g. a combined
     * session for JTG1 + JTG2) — empty array = no group target. */
    trainingGroupIds: z.array(uuid).default([]),
    /** Für diese Serie vorgesehene(r) Trainer (§ Nutzeranfrage) — nur
     * Mitglieder mit `members.isTrainer`, serverseitig geprüft
     * (`TrainingService.assertTrainers`). Materialisiert auf jeden generierten
     * Termin, siehe `training_series_assigned_trainers`/
     * `training_event_assigned_trainers`. Getrennt von `training_event_trainers`
     * (Abrechnungsbeleg des Selbst-Check-ins, siehe docs/trainers.md). */
    trainerMemberIds: z.array(uuid).default([]),
    /** Ob für diese Serie überhaupt ein Trainereinsatz vorgesehen ist
     * (§ Nutzeranfrage) — `false` sperrt das Selbst-/Admin-Check-in für jeden
     * generierten Termin (`TrainersService.loadCheckableEvent`) und blendet
     * diese Termine aus der Trainer-Arbeitsseite sowie der
     * Admin-Trainings-Übersicht aus (`listCheckableEvents`/
     * `listMonthlySessions`). Materialisiert auf jeden generierten Termin wie
     * `trainingGroupIds`/`trainerMemberIds`. */
    trainerPossible: z.boolean().default(true),
    /** § Nutzeranfrage: ob bei der Rückmeldung zu dieser Serie zusätzlich die
     * Option "Aktives Training" angeboten wird — `false` blendet die Checkbox
     * für alle generierten Termine aus (Standard). Materialisiert auf jeden
     * generierten Termin wie `trainerPossible`, siehe
     * `trainingEvents.activeTrainingOffered`. */
    activeTrainingOffered: z.boolean().default(false),
    teamId: uuid.nullable().optional(),
    venue: optText(200),
    hall: optText(120),
    frequency: z.enum(['weekly', 'interval']).default('weekly'),
    weekdays: z.array(z.number().int().min(1).max(7)).min(1),
    intervalWeeks: z.number().int().min(1).max(12).default(1),
    startDate: isoDate,
    endDate: isoDate.nullable().optional(),
    occurrenceCount: z.number().int().positive().max(400).nullable().optional(),
    startTime: hhmm,
    endTime: hhmm,
    maxParticipants: z.number().int().positive().nullable().optional(),
    registrationDeadlineHours: z.number().int().min(0).max(720).nullable().optional(),
    deregistrationDeadlineHours: z.number().int().min(0).max(720).nullable().optional(),
});
export const trainingSeriesWritable = trainingSeriesBase
    .refine((s) => s.endDate != null || s.occurrenceCount != null, {
    message: 'endDate oder occurrenceCount angeben',
    path: ['endDate'],
})
    // A typo'd end-date before the start date silently expands to zero
    // events (see expandRecurrence) — the series "creates" successfully but
    // nothing ever shows up, which looks like a bug. Reject it up front.
    .refine((s) => s.endDate == null || s.endDate >= s.startDate, {
    message: 'Enddatum darf nicht vor dem Startdatum liegen',
    path: ['endDate'],
});
export const trainingSeriesPatch = trainingSeriesBase.partial();
export const generateSeriesRequest = z.object({
    /** generate up to this date (defaults to series endDate or +120 days) */
    until: isoDate.optional(),
    /** delete future non-overridden events that fall outside the new plan */
    pruneOutside: z.boolean().default(false),
});
// ── training events ─────────────────────────────────────────────────────────
export const trainingEventWritable = z.object({
    title: z.string().trim().min(1).max(160),
    description: optText(2000),
    /** See `trainingSeriesBase.trainingGroupIds`. */
    trainingGroupIds: z.array(uuid).default([]),
    /** See `trainingSeriesBase.trainerMemberIds`. */
    trainerMemberIds: z.array(uuid).default([]),
    /** See `trainingSeriesBase.trainerPossible`. */
    trainerPossible: z.boolean().default(true),
    /** See `trainingSeriesBase.activeTrainingOffered`. */
    activeTrainingOffered: z.boolean().default(false),
    teamId: uuid.nullable().optional(),
    eventDate: isoDate,
    startTime: hhmm,
    endTime: hhmm,
    venue: optText(200),
    hall: optText(120),
    maxParticipants: z.number().int().positive().nullable().optional(),
    registrationDeadline: z.string().datetime().nullable().optional(),
    deregistrationDeadline: z.string().datetime().nullable().optional(),
    /** § Nutzeranfrage: optionaler Warnhinweis auf diesem einzelnen Termin. */
    warningText: optText(300),
    warningColor: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'Hex-Farbe, z. B. #fef3c7')
        .nullable()
        .optional(),
});
export const updateTrainingEventRequest = trainingEventWritable.partial().extend({
    status: z.enum(['scheduled', 'cancelled', 'completed', 'moved']).optional(),
    cancelledReason: optText(500),
});
export const trainingEventQuery = paginationQuery.extend({
    from: isoDate.optional(),
    to: isoDate.optional(),
    teamId: uuid.optional(),
    /** comma-separated training-group ids — matches events targeting any of them */
    trainingGroupIds: z.string().optional(),
    /** only events the caller (or a linked minor) is eligible for */
    mine: z.enum(['true', 'false']).optional(),
});
// ── responses & attendance ─────────────────────────────────────────────────
export const respondTrainingRequest = z.object({
    status: z.enum(['registered', 'declined', 'excused']),
    memberId: uuid.optional(), // defaults to the caller's own member
    comment: z.string().trim().max(500).optional(),
    /** § Nutzeranfrage: Haken "Aktives Training" bei der Anmeldung — nur
     * wirksam, wenn der Termin `activeTrainingOffered` ist, und nur bei
     * `status: 'registered'`; sonst serverseitig ignoriert/auf `false`
     * zurückgesetzt. */
    wantsActiveTraining: z.boolean().optional(),
});
export const recordAttendanceRequest = z.object({
    entries: z
        .array(z.object({
        memberId: uuid,
        status: z.enum(['attended', 'absent', 'excused']),
    }))
        .min(1),
});
export const addTrainingEventCommentRequest = z.object({
    comment: z.string().trim().min(1).max(2000),
    memberId: uuid.optional(), // defaults to the caller's own member, same as respondTrainingRequest
});
//# sourceMappingURL=training.js.map